import { useCallback, useMemo, useState } from "react";

import { createSelector } from "@reduxjs/toolkit";

import { useAppSelector } from "app/hooks";
import {
  selectCurrentClinic,
  selectCurrentRegion,
  selectCurrentUser,
  selectHasRegions
} from "features/auth/auth-slice";
import setupLocalStorageAPI from "utils/setupLocalStorageAPI";

const clinicFiltersStorage = setupLocalStorageAPI("clinic-filters");

const getFilters = () => clinicFiltersStorage.getData({});
const getCacheFilters = (cacheKey: string | null) =>
  cacheKey ? (getFilters()?.[cacheKey] ?? null) : null;

const selectStorageData = createSelector(
  [
    selectCurrentClinic,
    selectCurrentRegion,
    selectCurrentUser,
    selectHasRegions
  ],
  (currentClinic, currentRegion, currentUser, hasRegions) => ({
    currentClinic,
    currentRegion,
    currentUser,
    hasRegions
  })
);

export default function useStorage(disabled?: boolean) {
  const { currentClinic, currentRegion, currentUser, hasRegions } =
    useAppSelector(selectStorageData);

  const { fullKey } = useMemo(() => {
    if (disabled) return { savePerRegion: false, cacheKey: null, fullKey: null };
    const savePerRegion =
      !Boolean(currentUser?.scheduler_persist_per_clinic) && hasRegions;
    const cacheKey = savePerRegion ? currentRegion?.id : currentClinic?.id;
    const fullKey = cacheKey
      ? `${savePerRegion ? "region" : "clinic"}:${cacheKey}`
      : null;
    return { savePerRegion, cacheKey, fullKey };
  }, [
    disabled,
    currentUser?.scheduler_persist_per_clinic,
    hasRegions,
    currentRegion?.id,
    currentClinic?.id
  ]);

  // Incremented on each updateStorage call to invalidate the useMemo below,
  // ensuring we re-read localStorage after writes (fixes stale defaultStaff
  // when resetToDefaults persists new values within the same fullKey).
  const [storageVersion, setStorageVersion] = useState(0);

  const { defaultClinics, defaultStaff, defaultView } = useMemo(() => {
    if (disabled) {
      return { defaultClinics: null, defaultStaff: null, defaultView: null };
    }
    const defaultRegionFilters = getCacheFilters(fullKey ?? null);
    return {
      defaultClinics: defaultRegionFilters?.clinics ?? null,
      defaultStaff: defaultRegionFilters?.staff ?? null,
      defaultView: defaultRegionFilters?.view ?? null
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- storageVersion is a cache-buster for localStorage re-reads
  }, [disabled, fullKey, storageVersion]);

  const updateStorage = useCallback(
    (key: string, values: any) => {
      if (disabled || !fullKey) return;
      const filters = getFilters();
      const currentFiltersForKey = getCacheFilters(fullKey);

      const newState = { ...filters };
      newState[fullKey] = {
        ...currentFiltersForKey,
        [key]: values
      };
      clinicFiltersStorage.updateData(newState);
      setStorageVersion((v) => v + 1);
    },
    [disabled, fullKey]
  );

  return {
    defaultClinics,
    defaultStaff,
    defaultView,
    updateStorage,
    contextKey: disabled ? "noop" : fullKey
  };
}
